import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import type { Send } from '../../types/send';
import type { TimePeriod } from '../../utils/stats-utils';
import {
  ALL_STYLES,
  STYLE_COLORS,
  groupByPeriod,
  sortedPeriodKeys,
  periodLabel,
  CHART_TOOLTIP_STYLE,
} from '../../utils/stats-utils';

interface ClimbStyleEffortOverTimeProps {
  sends: Send[];
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
}

export default function ClimbStyleEffortOverTime({ sends, period, onPeriodChange }: ClimbStyleEffortOverTimeProps) {
  const { t } = useTranslation();

  const activeStyles = useMemo(() =>
    ALL_STYLES.filter(style =>
      sends.some(s => s.attributes.climb_style?.includes(style) && s.attributes.grade_opinion != null)
    ),
    [sends]
  );

  const data = useMemo(() => {
    const groups = groupByPeriod(sends, period);
    const keys = sortedPeriodKeys(groups);
    return keys.map(key => {
      const row: Record<string, string | number | null> = { label: periodLabel(key, period) };
      for (const style of activeStyles) {
        const styleSends = groups[key].filter(
          s => s.attributes.climb_style?.includes(style) && s.attributes.grade_opinion != null,
        );
        if (styleSends.length === 0) {
          row[style] = null;
        } else {
          const avg = styleSends.reduce((sum, s) => sum + (s.attributes.grade_opinion ?? 0.5), 0) / styleSends.length;
          row[style] = parseFloat((0.5 - avg).toFixed(3));
        }
      }
      return row;
    });
  }, [sends, period, activeStyles]);

  const isEmpty = data.length === 0 || activeStyles.length === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">{t('stats.climbStyleEffortOverTime.title')}</CardTitle>
        <ToggleGroup
          type="single"
          value={period}
          onValueChange={(v) => { if (v) onPeriodChange(v as TimePeriod); }}
          className="h-7"
        >
          <ToggleGroupItem value="week" className="h-7 px-2 text-xs">{t('stats.period.week')}</ToggleGroupItem>
          <ToggleGroupItem value="month" className="h-7 px-2 text-xs">{t('stats.period.month')}</ToggleGroupItem>
          <ToggleGroupItem value="year" className="h-7 px-2 text-xs">{t('stats.period.year')}</ToggleGroupItem>
        </ToggleGroup>
      </CardHeader>
      {isEmpty ? (
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t('stats.insufficientData')}
        </CardContent>
      ) : (
        <CardContent className="overflow-x-auto px-2 pb-4">
          <div style={{ minWidth: 320 }}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  domain={[-0.4, 0.4]}
                  ticks={[-0.4, -0.2, 0, 0.2, 0.4]}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => v.toFixed(1)}
                />
                <Tooltip
                  {...CHART_TOOLTIP_STYLE}
                  formatter={(value, name) => [
                    value != null ? Number(value).toFixed(2) : '—',
                    t(`climbTypes.${name as string}`),
                  ]}
                />
                <Legend
                  formatter={(value) => t(`climbTypes.${value}`)}
                  wrapperStyle={{ fontSize: 12 }}
                />
                <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1.5} />
                {activeStyles.map(style => (
                  <Line
                    key={style}
                    dataKey={style}
                    name={style}
                    stroke={STYLE_COLORS[style]}
                    strokeWidth={2}
                    dot={false}
                    connectNulls={true}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
